/**
 * Sign-up attribution: which ad, campaign or page brought each new account in.
 *
 * The landing pages (`Help-Genie-Landing-Pages/src/lib/attribution.ts`) capture
 * first-touch attribution on the same helpgenie.ai origin under the
 * localStorage key `hg_attribution_v1`. Voice now captures with the SAME rules
 * on its own routes too, so a visitor whose ad, email or social link lands
 * straight on an app page (not a marketing page) is not lost.
 *
 * Rules (keep in step with the landing-pages copy):
 * - First touch is written once and never overwritten, except by a new paid
 *   click id (gclid/gbraid/wbraid/fbclid/msclkid/ttclid/li_fat_id): that click
 *   is the one we are paying for.
 * - `last_touch` is refreshed on every tagged arrival (any click id or utm_*),
 *   so a later campaign is not lost behind an organic first visit.
 * - `utm_source=landing` is our own CTA tagging between pages, not a source.
 *   It is never captured as a touch; at sign-up it is kept as `signup_cta_*`.
 *
 * At sign-up `buildSignupMetadata()` adds everything else that joins the new
 * account to the rest of the data: the landing-pages visitor id (`hg_vid`,
 * same id as the lp_events log and Clarity), the GA4 client id, and the gclid
 * the Google tag stored in its `_gcl_aw` cookie (a second copy that survives
 * when the localStorage record is missing). It lands in
 * auth.users.raw_user_meta_data, which the Help Genie Command Center reads for
 * the funnel scorecard and the Google Ads offline-conversion upload.
 */

const KEY = "hg_attribution_v1";
const TTL_DAYS = 90;
const DAY_MS = 86_400_000;

export const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid", "li_fat_id"] as const;
export const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

type ClickId = (typeof CLICK_IDS)[number];
type Utm = (typeof UTMS)[number];

export type Touch = Partial<Record<ClickId | Utm, string>> & {
  landing_page?: string;
  referrer?: string;
  at?: string;
};

export interface LandingAttribution extends Partial<Record<ClickId | Utm, string>> {
  landing_page?: string;
  referrer?: string;
  device?: string;
  captured_at?: string;
  captured_by?: string;
  last_touch?: Touch;
}

const clip = (v: string | null | undefined, n = 200) => (v ? String(v).slice(0, n) : "");

function deviceClass(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

/**
 * A genie embedded on a customer's site runs in a third-party iframe; when the
 * browser blocks storage there, merely touching `localStorage` throws.
 */
function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readStored(): LandingAttribution | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LandingAttribution;
    if (parsed.captured_at && Date.now() - new Date(parsed.captured_at).getTime() > TTL_DAYS * DAY_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Click ids and utm_* in a query string. Our own `utm_source=landing` CTA tags are not a touch. */
export function touchFromSearch(search: string): Touch {
  const params = new URLSearchParams(search);
  const touch: Touch = {};
  for (const k of CLICK_IDS) {
    const v = params.get(k);
    if (v) touch[k] = clip(v);
  }
  if (params.get("utm_source") !== "landing") {
    for (const k of UTMS) {
      const v = params.get(k);
      if (v) touch[k] = clip(v);
    }
  }
  return touch;
}

const hasPaidClick = (t: Touch) => CLICK_IDS.some((k) => Boolean(t[k]));

/**
 * Call on each app page load. Writes only when the URL carries a click id or
 * a real utm_* (never on untagged in-app navigation), with the rules above.
 */
export function captureAppAttribution(): LandingAttribution | null {
  if (typeof window === "undefined") return null;
  const storage = getStorage();
  if (!storage) return null;
  const fresh = touchFromSearch(window.location.search);
  if (Object.keys(fresh).length === 0) return readStored();

  const now = new Date().toISOString();
  const page = clip(window.location.pathname + window.location.search, 500);
  const referrer = clip(document.referrer, 500);
  const existing = readStored();
  const lastTouch: Touch = { ...fresh, landing_page: page, referrer, at: now };

  let next: LandingAttribution;
  if (!existing || hasPaidClick(fresh)) {
    next = { ...fresh, landing_page: page, referrer, device: deviceClass(), captured_at: now, captured_by: "voice", last_touch: lastTouch };
  } else {
    next = { ...existing, last_touch: lastTouch };
  }
  try {
    storage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private browsing or storage full: sign-up still works without it */
  }
  return next;
}

/** Flat first-touch fields (no `last_touch`), safe to spread into a GA4 event. */
export function getLandingAttribution(): LandingAttribution {
  const stored = readStored();
  if (!stored) return {};
  const { last_touch: _lastTouch, ...flat } = stored;
  return flat;
}

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&")}=([^;]*)`));
  if (!m) return "";
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/** `_gcl_aw=GCL.<unix seconds>.<gclid>` (or `_gcl_gb` for gbraid), written by the Google tag. */
export function parseGclCookie(value: string): { id: string; at: string } | null {
  const m = /^GCL\.(\d+)\.(.+)$/.exec(value || "");
  if (!m) return null;
  const secs = Number(m[1]);
  return { id: m[2].slice(0, 200), at: Number.isFinite(secs) ? new Date(secs * 1000).toISOString() : "" };
}

/** `_ga=GA1.1.<random>.<timestamp>` → the GA4 client id `<random>.<timestamp>`. */
export function parseGaClientId(value: string): string {
  const m = /^GA\d\.\d+\.(\d+\.\d+)$/.exec(value || "");
  return m ? m[1] : "";
}

function readLocal(key: string): string {
  try {
    return getStorage()?.getItem(key) || "";
  } catch {
    return "";
  }
}

/**
 * Everything we can attach to a new account at sign-up, flat keys only (the
 * Command Center reads them straight off auth user_metadata). First-touch
 * fields keep their original names; the rest are prefixed so nothing collides.
 */
export function buildSignupMetadata(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  captureAppAttribution();
  const out: Record<string, unknown> = {};
  const set = (k: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  };

  const stored = readStored();
  if (stored) {
    const { last_touch: lastTouch, ...first } = stored;
    for (const [k, v] of Object.entries(first)) set(k, v);
    if (lastTouch) for (const [k, v] of Object.entries(lastTouch)) set(`last_${k}`, v);
  }

  // Which button sent them: our own CTA tagging on the sign-up URL.
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source") === "landing") {
    set("signup_cta_medium", clip(params.get("utm_medium")));
    set("signup_cta_campaign", clip(params.get("utm_campaign")));
    set("signup_cta_content", clip(params.get("utm_content")));
  }
  set("signup_page", clip(window.location.pathname, 200));
  set("signup_referrer", clip(document.referrer, 500));

  // Join keys: landing-pages visitor id (lp_events, Clarity, bookings) and GA4.
  set("hg_vid", clip(readLocal("hg_vid"), 64));
  set("hg_first_visit", clip(readLocal("hg_first_visit"), 40));
  const visits = Number(readLocal("hg_visits"));
  if (Number.isFinite(visits) && visits > 0) set("hg_visits", visits);
  set("ga_client_id", parseGaClientId(readCookie("_ga")));

  // The Google tag's own copy of the ad click (90-day cookie on helpgenie.ai).
  const aw = parseGclCookie(readCookie("_gcl_aw"));
  if (aw) {
    set("gcl_aw", aw.id);
    set("gcl_aw_at", aw.at);
  }
  const gb = parseGclCookie(readCookie("_gcl_gb"));
  if (gb) {
    set("gcl_gb", gb.id);
    set("gcl_gb_at", gb.at);
  }

  try {
    set("signup_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  } catch {
    /* no Intl */
  }
  set("signup_lang", clip(typeof navigator !== "undefined" ? navigator.language : "", 20));
  set("signup_device", deviceClass());
  set("attribution_version", 2);
  return out;
}
